import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const policyError = (e) => {
  const d = e.response?.data?.detail;
  if (d && typeof d === "object") return `${d.message}${d.fix ? ` Fix: ${d.fix}` : ""}`;
  return d || e.message;
};

/** In-page review for activating / re-holding external transactional email. Success only when the server read-back is verified. */
export const ActivationDialog = ({ open, onOpenChange, mode, rd, onDone }) => {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const activating = mode === "activate";
  const blocked = activating && !rd.ready;
  const canSubmit = reason.trim().length >= 5 && (!activating || ack) && !blocked && !busy;
  const close = (v) => { if (!v) { setReason(""); setAck(false); setError(null); } onOpenChange(v); };
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.put("/comms/sender/policy", { hold_all: !activating, reason: reason.trim() });
      const ok = data.verified && (activating ? data.effective_live : !data.effective_live);
      if (!ok) throw new Error(`Read-back shows ${data.effective} — not confirmed`);
      toast.success(activating ? "Verified: external transactional email is LIVE (read back from the server)" : "Verified: external email is ON HOLD");
      close(false); onDone();
    } catch (e) { setError(policyError(e)); onDone(); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="eh-activation-dialog">
        <DialogHeader>
          <DialogTitle>{activating ? "Activate live transactional email" : "Put external email back on hold"}</DialogTitle>
          <DialogDescription>{activating ? "Review the scope before you approve." : "External recipients will stop receiving email until a super admin activates again."}</DialogDescription>
        </DialogHeader>
        {activating && (
          <div className="space-y-1.5 rounded-md bg-slate-50 p-3 text-[12px]" data-testid="eh-dialog-scope">
            <div><b>Scope:</b> {rd.activation.scope}</div>
            <div><b>Excludes:</b> {rd.activation.excludes}</div>
            <div className="font-semibold text-slate-900" data-testid="eh-dialog-no-backlog"><b>Backlog:</b> {rd.activation.effect}</div>
          </div>
        )}
        {blocked && (
          <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-[12px] text-rose-800" data-testid="eh-dialog-blocked">
            <b>Activation is blocked.</b> {rd.unmet.map((u, i) => <div key={i}>• {u}</div>)}
          </div>
        )}
        <label className="text-[12px] font-semibold" htmlFor="eh-reason">Approval reason (required)</label>
        <Textarea id="eh-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Owner approved future transactional sends only" className="text-[12px]" data-testid="eh-dialog-reason" />
        {activating && (
          <label className="flex items-start gap-2 text-[12px]" htmlFor="eh-ack">
            <Checkbox id="eh-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="eh-dialog-ack" />
            <span>I understand only future transactional email is sent; nothing held or sent earlier will be resent.</span>
          </label>
        )}
        {error && <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-[12px] text-rose-800" data-testid="eh-dialog-error">{error}</div>}
        <DialogFooter>
          <button onClick={() => close(false)} className="h-9 rounded-full border border-slate-300 px-4 text-[12px]" data-testid="eh-dialog-cancel">Cancel</button>
          <button onClick={submit} disabled={!canSubmit} className="h-9 rounded-full bg-slate-900 px-4 text-[12px] font-semibold text-white disabled:opacity-40" data-testid="eh-dialog-confirm">{busy ? "Verifying…" : activating ? "Approve and activate" : "Put on hold"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
